import React, { useState, useEffect } from 'react';
import { getTenantToday } from '../../utils/dateUtils';
import styled, { keyframes } from 'styled-components';
import axios from 'axios';
import { Edit2, Trash2, X, Plus, Users, Link2, Unlink } from 'lucide-react';

interface ModelPortfolio {
  id: number;
  name: string;
  code: string;
  description: string;
  strategy: 'INCOME' | 'GROWTH' | 'BALANCED' | 'AGGRESSIVE' | 'CONSERVATIVE';
  riskLevel: number; // 1-10
  targetReturn: number;
  benchmarkIndex: string;
  aum: number;
  accountCount: number;
  driftThreshold: number;
  currentDrift: number;
  lastRebalanced: string;
  status: 'ACTIVE' | 'INACTIVE' | 'DRAFT';
  allocations: Allocation[];
}

interface Allocation {
  assetClass: string;
  targetPercent: number;
  currentPercent: number;
  minPercent: number;
  maxPercent: number;
}

interface Portfolio {
  id: number;
  portfolioName: string;
  portfolioCode: string;
  aum: number;
  riskTolerance?: string;
  householdName?: string;
}

interface AssignedPortfolio {
  assignmentId: number;
  portfolioId: number;
  portfolioName: string;
  portfolioCode: string;
  assignmentType: 'FULL' | 'SLEEVE';
  sleevePercentage?: number;
  assignedAt: string;
  aum: number;
}

const slideUp = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const Container = styled.div<{ embedded?: boolean }>`
  padding: ${props => props.embedded ? '1.5rem' : '2rem'};
  background: ${props => props.embedded ? 'transparent' : 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)'};
  min-height: ${props => props.embedded ? 'auto' : 'calc(100vh - 64px)'};
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
`;

const Title = styled.h1`
  font-size: 1.75rem;
  font-weight: 700;
  color: #ffffff;
  margin: 0;
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' | 'danger' }>`
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  ${props => props.variant === 'primary' ? `
    background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
    color: white;
    border: none;
    &:hover {
      background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
    }
  ` : props.variant === 'danger' ? `
    background: rgba(239, 68, 68, 0.1);
    color: #ef4444;
    border: 1px solid rgba(239, 68, 68, 0.3);
    &:hover {
      background: rgba(239, 68, 68, 0.2);
    }
  ` : `
    background: rgba(255, 255, 255, 0.1);
    color: #e2e8f0;
    border: 1px solid rgba(255, 255, 255, 0.2);
    &:hover {
      background: rgba(255, 255, 255, 0.15);
    }
  `}
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
`;

const StatCard = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 1.5rem;
`;

const StatValue = styled.div`
  font-size: 2rem;
  font-weight: 700;
  color: #ffffff;
  margin-bottom: 0.25rem;
`;

const StatLabel = styled.div`
  font-size: 0.875rem;
  color: #94a3b8;
`;

const ModelsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(380px, 1fr));
  gap: 1.5rem;
`;

const ModelCard = styled.div<{ hasDrift?: boolean }>`
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid ${props => props.hasDrift ? 'rgba(234, 179, 8, 0.5)' : 'rgba(255, 255, 255, 0.1)'};
  border-radius: 16px;
  padding: 1.5rem;
  transition: all 0.2s;
  position: relative;

  &:hover {
    background: rgba(255, 255, 255, 0.05);
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
  }
`;

const CardActions = styled.div`
  position: absolute;
  top: 1rem;
  right: 1rem;
  display: flex;
  gap: 0.5rem;
  opacity: 0;
  transition: opacity 0.2s;

  ${ModelCard}:hover & {
    opacity: 1;
  }
`;

const CardActionButton = styled.button<{ variant?: 'edit' | 'delete' }>`
  padding: 0.5rem;
  border-radius: 6px;
  border: none;
  cursor: pointer;
  transition: all 0.2s;
  background: ${props => props.variant === 'delete' ? 'rgba(239, 68, 68, 0.1)' : 'rgba(59, 130, 246, 0.1)'};
  color: ${props => props.variant === 'delete' ? '#ef4444' : '#3b82f6'};

  &:hover {
    background: ${props => props.variant === 'delete' ? 'rgba(239, 68, 68, 0.2)' : 'rgba(59, 130, 246, 0.2)'};
  }
`;

const ModelHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1rem;
  padding-right: 4rem;
`;

const ModelInfo = styled.div``;

const ModelName = styled.h3`
  font-size: 1.25rem;
  font-weight: 700;
  color: #ffffff;
  margin: 0 0 0.25rem 0;
`;

const ModelCode = styled.span`
  font-size: 0.75rem;
  color: #64748b;
  font-family: monospace;
`;

const StrategyBadge = styled.span<{ strategy: string }>`
  padding: 0.25rem 0.75rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 600;

  ${props => {
    switch (props.strategy) {
      case 'GROWTH':
        return 'background: rgba(34, 197, 94, 0.2); color: #22c55e;';
      case 'INCOME':
        return 'background: rgba(59, 130, 246, 0.2); color: #3b82f6;';
      case 'BALANCED':
        return 'background: rgba(168, 85, 247, 0.2); color: #a855f7;';
      case 'AGGRESSIVE':
        return 'background: rgba(239, 68, 68, 0.2); color: #ef4444;';
      case 'CONSERVATIVE':
        return 'background: rgba(148, 163, 184, 0.2); color: #94a3b8;';
      default:
        return 'background: rgba(148, 163, 184, 0.2); color: #94a3b8;';
    }
  }}
`;

const ModelStats = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
  margin: 1rem 0;
  padding: 1rem 0;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
`;

const ModelStat = styled.div`
  text-align: center;
`;

const ModelStatValue = styled.div`
  font-size: 1.25rem;
  font-weight: 700;
  color: #ffffff;
`;

const ModelStatLabel = styled.div`
  font-size: 0.7rem;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const AllocationBar = styled.div`
  display: flex;
  height: 8px;
  border-radius: 4px;
  overflow: hidden;
  margin-top: 1rem;
`;

const AllocationSegment = styled.div<{ color: string; width: number }>`
  width: ${props => props.width}%;
  background: ${props => props.color};
`;

const AllocationLegend = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-top: 0.75rem;
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.7rem;
  color: #94a3b8;
`;

const LegendDot = styled.div<{ color: string }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${props => props.color};
`;

const DriftWarning = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  background: rgba(234, 179, 8, 0.1);
  border-radius: 8px;
  font-size: 0.75rem;
  color: #eab308;
  margin-top: 1rem;
`;

const AssignButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: rgba(139, 92, 246, 0.1);
  border: 1px solid rgba(139, 92, 246, 0.3);
  border-radius: 8px;
  color: #a78bfa;
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  margin-top: 1rem;

  &:hover {
    background: rgba(139, 92, 246, 0.2);
    border-color: rgba(139, 92, 246, 0.5);
  }
`;

const AssignedPortfoliosList = styled.div`
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
`;

const AssignedPortfolioItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 0.75rem;
  background: rgba(255, 255, 255, 0.02);
  border-radius: 6px;
  margin-bottom: 0.5rem;
  font-size: 0.8rem;

  &:last-child {
    margin-bottom: 0;
  }
`;

const PortfolioInfo = styled.div`
  display: flex;
  flex-direction: column;
`;

const PortfolioName = styled.span`
  color: #e2e8f0;
  font-weight: 500;
`;

const PortfolioMeta = styled.span`
  color: #64748b;
  font-size: 0.7rem;
`;

const UnassignButton = styled.button`
  padding: 0.25rem 0.5rem;
  background: rgba(239, 68, 68, 0.1);
  border: none;
  border-radius: 4px;
  color: #ef4444;
  font-size: 0.7rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.25rem;

  &:hover {
    background: rgba(239, 68, 68, 0.2);
  }
`;

const AssignedCount = styled.div`
  font-size: 0.7rem;
  color: #64748b;
  margin-bottom: 0.5rem;
`;

// Assignment Modal specific styles
const PortfolioListContainer = styled.div`
  max-height: 300px;
  overflow-y: auto;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  margin-bottom: 1rem;
`;

const PortfolioSelectItem = styled.div<{ selected?: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  cursor: pointer;
  transition: all 0.2s;
  background: ${props => props.selected ? 'rgba(139, 92, 246, 0.15)' : 'transparent'};

  &:hover {
    background: ${props => props.selected ? 'rgba(139, 92, 246, 0.2)' : 'rgba(255, 255, 255, 0.05)'};
  }

  &:last-child {
    border-bottom: none;
  }
`;

const PortfolioCheckbox = styled.div<{ checked?: boolean }>`
  width: 20px;
  height: 20px;
  border-radius: 4px;
  border: 2px solid ${props => props.checked ? '#8b5cf6' : 'rgba(255, 255, 255, 0.2)'};
  background: ${props => props.checked ? '#8b5cf6' : 'transparent'};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 0.75rem;
`;

const PortfolioDetails = styled.div`
  flex: 1;
`;

const PortfolioAum = styled.span`
  color: #22c55e;
  font-weight: 600;
  font-size: 0.8rem;
`;

const AssignmentTypeSelect = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 1rem;
`;

const AssignmentTypeOption = styled.div<{ selected?: boolean }>`
  flex: 1;
  padding: 1rem;
  border: 2px solid ${props => props.selected ? '#8b5cf6' : 'rgba(255, 255, 255, 0.1)'};
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
  background: ${props => props.selected ? 'rgba(139, 92, 246, 0.1)' : 'transparent'};

  &:hover {
    border-color: ${props => props.selected ? '#8b5cf6' : 'rgba(255, 255, 255, 0.3)'};
  }
`;

const AssignmentTypeTitle = styled.div`
  font-weight: 600;
  color: #e2e8f0;
  margin-bottom: 0.25rem;
`;

const AssignmentTypeDesc = styled.div`
  font-size: 0.75rem;
  color: #64748b;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 0.75rem 1rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  background: rgba(15, 23, 42, 0.6);
  color: #e2e8f0;
  font-size: 0.875rem;
  margin-bottom: 1rem;

  &:focus {
    outline: none;
    border-color: #8b5cf6;
  }

  &::placeholder {
    color: #64748b;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 2rem;
  color: #64748b;
`;

// Modal Styles
const ModalBackdrop = styled.div<{ show: boolean }>`
  display: ${props => props.show ? 'flex' : 'none'};
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(8px);
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalCard = styled.div`
  background: #0f172a;
  border-radius: 24px;
  width: 90%;
  max-width: 700px;
  max-height: 90vh;
  overflow: hidden;
  box-shadow: 0 25px 80px rgba(0,0,0,0.3);
  border: 1px solid rgba(255, 255, 255, 0.1);
  animation: ${slideUp} 0.3s ease-out;
`;

const ModalHeader = styled.div`
  padding: 24px;
  border-bottom: 1px solid rgba(148, 163, 184, 0.2);
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
`;

const ModalTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 700;
  color: #e2e8f0;
  margin: 0;
`;

const ModalClose = styled.button`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: none;
  background: rgba(15, 23, 42, 0.4);
  color: #94a3b8;
  cursor: pointer;
  font-size: 1.25rem;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: rgba(15, 23, 42, 0.6);
    color: #ffffff;
  }
`;

const ModalBody = styled.div`
  padding: 24px;
  overflow-y: auto;
  max-height: calc(90vh - 160px);
`;

const ModalFooter = styled.div`
  padding: 16px 24px;
  border-top: 1px solid rgba(148, 163, 184, 0.2);
  display: flex;
  justify-content: flex-end;
  gap: 12px;
`;

const FormGroup = styled.div`
  margin-bottom: 1.25rem;
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
`;

const Label = styled.label`
  display: block;
  font-size: 0.75rem;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 8px;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 16px;
  border: 2px solid rgba(148, 163, 184, 0.2);
  border-radius: 12px;
  font-size: 0.9375rem;
  background: rgba(15, 23, 42, 0.6);
  color: #e2e8f0;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 4px rgba(102, 126, 234, 0.1);
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 12px 16px;
  border: 2px solid rgba(148, 163, 184, 0.2);
  border-radius: 12px;
  font-size: 0.9375rem;
  background: rgba(30, 41, 59, 0.6);
  color: #e2e8f0;
  cursor: pointer;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: #667eea;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 12px 16px;
  border: 2px solid rgba(148, 163, 184, 0.2);
  border-radius: 12px;
  font-size: 0.9375rem;
  background: rgba(15, 23, 42, 0.6);
  color: #e2e8f0;
  transition: all 0.2s;
  resize: vertical;
  min-height: 80px;

  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 4px rgba(102, 126, 234, 0.1);
  }
`;

const formatCurrency = (value: number) => {
  if (value >= 1000000) {
    return `$${(value / 1000000).toFixed(1)}M`;
  }
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

const ALLOCATION_COLORS: Record<string, string> = {
  'US Equity': '#3b82f6',
  'International Equity': '#8b5cf6',
  'Fixed Income': '#22c55e',
  'Real Estate': '#f97316',
  'Alternatives': '#ec4899',
  'Cash': '#94a3b8',
  'Private Credit': '#14b8a6',
  'Commodities': '#eab308',
};

interface ModelPortfoliosScreenProps {
  embedded?: boolean;
  triggerNew?: number; // When this changes, open the new model modal
}

const ModelPortfoliosScreen: React.FC<ModelPortfoliosScreenProps> = ({ embedded = false, triggerNew = 0 }) => {
  const [models, setModels] = useState<ModelPortfolio[]>([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedModel, setSelectedModel] = useState<ModelPortfolio | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    description: '',
    strategy: 'BALANCED' as ModelPortfolio['strategy'],
    riskLevel: 5,
    targetReturn: 7.0,
    benchmarkIndex: 'S&P 500',
    driftThreshold: 5,
    status: 'ACTIVE' as ModelPortfolio['status'],
  });

  // Assignment modal state
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [assigningModel, setAssigningModel] = useState<ModelPortfolio | null>(null);
  const [availablePortfolios, setAvailablePortfolios] = useState<Portfolio[]>([]);
  const [assignedPortfolios, setAssignedPortfolios] = useState<Record<number, AssignedPortfolio[]>>({});
  const [selectedPortfolioIds, setSelectedPortfolioIds] = useState<number[]>([]);
  const [assignmentType, setAssignmentType] = useState<'FULL' | 'SLEEVE'>('FULL');
  const [sleevePercentage, setSleevePercentage] = useState(100);
  const [portfolioSearch, setPortfolioSearch] = useState('');
  const [assigningInProgress, setAssigningInProgress] = useState(false);

  // Mock data
  const mockModels: ModelPortfolio[] = [
    {
      id: 1,
      name: 'Conservative Income',
      code: 'CONS-INC-01',
      description: 'Low-risk portfolio focused on income generation',
      strategy: 'CONSERVATIVE',
      riskLevel: 3,
      targetReturn: 4.5,
      benchmarkIndex: 'Bloomberg Aggregate Bond',
      aum: 45000000,
      accountCount: 125,
      driftThreshold: 5,
      currentDrift: 2.3,
      lastRebalanced: '2024-01-15',
      status: 'ACTIVE',
      allocations: [
        { assetClass: 'Fixed Income', targetPercent: 60, currentPercent: 58.5, minPercent: 55, maxPercent: 65 },
        { assetClass: 'US Equity', targetPercent: 20, currentPercent: 21.2, minPercent: 15, maxPercent: 25 },
        { assetClass: 'Real Estate', targetPercent: 10, currentPercent: 10.3, minPercent: 5, maxPercent: 15 },
        { assetClass: 'Cash', targetPercent: 10, currentPercent: 10, minPercent: 5, maxPercent: 15 },
      ],
    },
    {
      id: 2,
      name: 'Balanced Growth',
      code: 'BAL-GRW-01',
      description: 'Moderate risk with balanced equity and fixed income',
      strategy: 'BALANCED',
      riskLevel: 5,
      targetReturn: 7.0,
      benchmarkIndex: '60/40 Benchmark',
      aum: 78000000,
      accountCount: 210,
      driftThreshold: 5,
      currentDrift: 6.1,
      lastRebalanced: '2023-12-01',
      status: 'ACTIVE',
      allocations: [
        { assetClass: 'US Equity', targetPercent: 40, currentPercent: 44.2, minPercent: 35, maxPercent: 45 },
        { assetClass: 'International Equity', targetPercent: 15, currentPercent: 16.8, minPercent: 10, maxPercent: 20 },
        { assetClass: 'Fixed Income', targetPercent: 35, currentPercent: 31.0, minPercent: 30, maxPercent: 40 },
        { assetClass: 'Alternatives', targetPercent: 5, currentPercent: 4.5, minPercent: 0, maxPercent: 10 },
        { assetClass: 'Cash', targetPercent: 5, currentPercent: 3.5, minPercent: 2, maxPercent: 10 },
      ],
    },
    {
      id: 3,
      name: 'Aggressive Growth',
      code: 'AGG-GRW-01',
      description: 'High-risk portfolio targeting maximum growth',
      strategy: 'AGGRESSIVE',
      riskLevel: 8,
      targetReturn: 10.0,
      benchmarkIndex: 'S&P 500',
      aum: 32000000,
      accountCount: 85,
      driftThreshold: 5,
      currentDrift: 1.8,
      lastRebalanced: '2024-01-10',
      status: 'ACTIVE',
      allocations: [
        { assetClass: 'US Equity', targetPercent: 55, currentPercent: 54.2, minPercent: 50, maxPercent: 60 },
        { assetClass: 'International Equity', targetPercent: 25, currentPercent: 26.0, minPercent: 20, maxPercent: 30 },
        { assetClass: 'Alternatives', targetPercent: 15, currentPercent: 14.8, minPercent: 10, maxPercent: 20 },
        { assetClass: 'Cash', targetPercent: 5, currentPercent: 5.0, minPercent: 2, maxPercent: 10 },
      ],
    },
    {
      id: 4,
      name: 'Income Focus',
      code: 'INC-FOC-01',
      description: 'Dividend and interest income generation',
      strategy: 'INCOME',
      riskLevel: 4,
      targetReturn: 5.5,
      benchmarkIndex: 'MSCI High Dividend Yield',
      aum: 55000000,
      accountCount: 150,
      driftThreshold: 5,
      currentDrift: 3.2,
      lastRebalanced: '2024-01-08',
      status: 'ACTIVE',
      allocations: [
        { assetClass: 'Fixed Income', targetPercent: 45, currentPercent: 43.5, minPercent: 40, maxPercent: 50 },
        { assetClass: 'US Equity', targetPercent: 30, currentPercent: 31.2, minPercent: 25, maxPercent: 35 },
        { assetClass: 'Real Estate', targetPercent: 15, currentPercent: 16.3, minPercent: 10, maxPercent: 20 },
        { assetClass: 'Private Credit', targetPercent: 5, currentPercent: 4.5, minPercent: 0, maxPercent: 10 },
        { assetClass: 'Cash', targetPercent: 5, currentPercent: 4.5, minPercent: 2, maxPercent: 10 },
      ],
    },
  ];

  useEffect(() => {
    const fetchModels = async () => {
      try {
        const response = await axios.get('/api/advisor/models/all');
        // Transform API data to match our interface
        const transformedModels = (Array.isArray(response.data) ? response.data : []).map((m: any) => ({
          id: m.id,
          name: m.modelName || 'Unnamed Model',
          code: m.modelCode || `MODEL-${m.id}`,
          description: m.description || '',
          strategy: (m.riskProfile || 'BALANCED').toUpperCase(),
          riskLevel: m.riskLevel || 5,
          targetReturn: m.targetReturnPct || 7.0,
          benchmarkIndex: m.primaryBenchmark || 'S&P 500',
          aum: parseFloat(m.totalModelAum) || 0,
          accountCount: m.assignedAccountCount || 0,
          driftThreshold: parseFloat(m.driftThresholdPct) || 5,
          currentDrift: parseFloat(m.currentDriftPct) || 0,
          lastRebalanced: m.lastRebalancedDate || '',
          status: (m.isActive ? 'ACTIVE' : 'INACTIVE') as 'ACTIVE' | 'INACTIVE' | 'DRAFT',
          allocations: (m.allocations || []).map((a: any) => ({
            assetClass: a.assetClass || a.sector || a.cusip || 'Unknown',
            targetPercent: parseFloat(a.targetWeightPct) || 0,
            currentPercent: parseFloat(a.currentWeightPct) || parseFloat(a.targetWeightPct) || 0,
            minPercent: parseFloat(a.minWeightPct) || 0,
            maxPercent: parseFloat(a.maxWeightPct) || 100,
          }))
        }));
        setModels(transformedModels);
      } catch (error) {
        console.error('Error fetching model portfolios:', error);
        // Show empty state instead of mock data
        setModels([]);
      } finally {
        setLoading(false);
      }
    };

    fetchModels();
  }, []);

  // Watch for triggerNew changes to open the modal
  useEffect(() => {
    if (triggerNew > 0) {
      setSelectedModel(null);
      setFormData({
        name: '',
        code: '',
        description: '',
        strategy: 'BALANCED',
        riskLevel: 5,
        targetReturn: 7.0,
        benchmarkIndex: 'S&P 500',
        driftThreshold: 5,
        status: 'ACTIVE',
      });
      setIsModalOpen(true);
    }
  }, [triggerNew]);

  const totalAum = models.reduce((sum, m) => sum + m.aum, 0);
  const totalAccounts = models.reduce((sum, m) => sum + m.accountCount, 0);
  const modelsWithDrift = models.filter(m => m.currentDrift > m.driftThreshold).length;

  const handleEdit = (model: ModelPortfolio) => {
    setSelectedModel(model);
    setFormData({
      name: model.name,
      code: model.code,
      description: model.description,
      strategy: model.strategy,
      riskLevel: model.riskLevel,
      targetReturn: model.targetReturn,
      benchmarkIndex: model.benchmarkIndex,
      driftThreshold: model.driftThreshold,
      status: model.status,
    });
    setIsModalOpen(true);
  };

  const handleDelete = async (model: ModelPortfolio) => {
    if (!window.confirm(`Are you sure you want to delete "${model.name}"?`)) return;

    try {
      await axios.delete(`/api/advisor/models/${model.id}`);
      setModels(prev => prev.filter(m => m.id !== model.id));
    } catch (error) {
      // In demo mode, just remove from local state
      setModels(prev => prev.filter(m => m.id !== model.id));
    }
  };

  const handleSave = async () => {
    try {
      if (selectedModel) {
        // Update existing model
        await axios.put(`/api/advisor/models/${selectedModel.id}`, formData);
        setModels(prev => prev.map(m =>
          m.id === selectedModel.id
            ? { ...m, ...formData }
            : m
        ));
      } else {
        // Create new model
        const response = await axios.post('/api/advisor/models', formData);
        setModels(prev => [...prev, response.data]);
      }
      setIsModalOpen(false);
    } catch (error) {
      // In demo mode, add to local state with mock ID
      if (!selectedModel) {
        const newModel: ModelPortfolio = {
          id: Math.max(...models.map(m => m.id), 0) + 1,
          ...formData,
          aum: 0,
          accountCount: 0,
          currentDrift: 0,
          lastRebalanced: getTenantToday(),
          allocations: [],
        };
        setModels(prev => [...prev, newModel]);
      } else {
        setModels(prev => prev.map(m =>
          m.id === selectedModel.id
            ? { ...m, ...formData }
            : m
        ));
      }
      setIsModalOpen(false);
    }
  };

  const openNewModelModal = () => {
    setSelectedModel(null);
    setFormData({
      name: '',
      code: '',
      description: '',
      strategy: 'BALANCED',
      riskLevel: 5,
      targetReturn: 7.0,
      benchmarkIndex: 'S&P 500',
      driftThreshold: 5,
      status: 'ACTIVE',
    });
    setIsModalOpen(true);
  };

  // Fetch available portfolios for assignment
  const fetchAvailablePortfolios = async () => {
    try {
      const response = await axios.get('/api/dimensions/portfolios');
      // Ensure we always set an array
      const data = Array.isArray(response.data) ? response.data :
                   (response.data?.content && Array.isArray(response.data.content)) ? response.data.content : [];
      // Transform portfolio data to match our interface
      const transformedPortfolios = data.map((p: any) => ({
        id: p.id,
        portfolioName: p.portfolioName || p.portfolio_name || 'Unknown',
        portfolioCode: p.portfolioCode || p.portfolio_code || p.accountNumber || '',
        aum: parseFloat(p.currentValue) || parseFloat(p.current_value) || 0,
        riskTolerance: p.riskTolerance || 'MODERATE',
        householdName: p.householdName || ''
      }));
      setAvailablePortfolios(transformedPortfolios);
    } catch (error) {
      console.error('Error fetching portfolios:', error);
      // Show empty state instead of mock data
      setAvailablePortfolios([]);
    }
  };

  // Fetch assigned portfolios for a model
  const fetchAssignedPortfolios = async (modelId: number) => {
    try {
      // Use the new endpoint that returns full portfolio details
      const response = await axios.get(`/api/advisor/models/${modelId}/portfolios/details`);
      const data = Array.isArray(response.data) ? response.data : [];
      const transformed = data.map((a: any) => ({
        assignmentId: a.assignmentId || a.id,
        portfolioId: a.portfolioId,
        portfolioName: a.portfolioName || 'Unknown',
        portfolioCode: a.portfolioCode || '',
        assignmentType: a.assignmentType || 'FULL',
        sleevePercentage: parseFloat(a.sleevePercentage) || undefined,
        assignedAt: a.effectiveDate || a.createdAt || '',
        aum: parseFloat(a.aum) || 0,
      }));
      setAssignedPortfolios(prev => ({ ...prev, [modelId]: transformed }));
    } catch (error) {
      console.error(`Error fetching assigned portfolios for model ${modelId}:`, error);
      // Show empty assignments instead of mock data
      setAssignedPortfolios(prev => ({ ...prev, [modelId]: [] }));
    }
  };

  // Open assignment modal
  const openAssignModal = async (model: ModelPortfolio) => {
    setAssigningModel(model);
    setSelectedPortfolioIds([]);
    setAssignmentType('FULL');
    setSleevePercentage(100);
    setPortfolioSearch('');
    await fetchAvailablePortfolios();
    await fetchAssignedPortfolios(model.id);
    setIsAssignModalOpen(true);
  };

  // Handle portfolio selection toggle
  const togglePortfolioSelection = (portfolioId: number) => {
    setSelectedPortfolioIds(prev =>
      prev.includes(portfolioId)
        ? prev.filter(id => id !== portfolioId)
        : [...prev, portfolioId]
    );
  };

  // Assign selected portfolios to model
  const handleAssignPortfolios = async () => {
    if (!assigningModel || selectedPortfolioIds.length === 0) return;

    setAssigningInProgress(true);
    try {
      for (const portfolioId of selectedPortfolioIds) {
        await axios.post(`/api/advisor/models/${assigningModel.id}/assign/${portfolioId}`, {
          assignmentType,
          sleevePercentage: assignmentType === 'SLEEVE' ? sleevePercentage : null,
        });
      }
      await fetchAssignedPortfolios(assigningModel.id);
      setSelectedPortfolioIds([]);
      window.alert(`Successfully assigned ${selectedPortfolioIds.length} portfolio(s) to ${assigningModel.name}`);
    } catch (error) {
      // Mock success for demo
      const newAssignments: AssignedPortfolio[] = selectedPortfolioIds.map((pid, idx) => {
        const portfolio = availablePortfolios.find(p => p.id === pid);
        return {
          assignmentId: Date.now() + idx,
          portfolioId: pid,
          portfolioName: portfolio?.portfolioName || 'Unknown',
          portfolioCode: portfolio?.portfolioCode || '',
          assignmentType,
          sleevePercentage: assignmentType === 'SLEEVE' ? sleevePercentage : undefined,
          assignedAt: getTenantToday(),
          aum: portfolio?.aum || 0,
        };
      });
      setAssignedPortfolios(prev => ({
        ...prev,
        [assigningModel.id]: [...(prev[assigningModel.id] || []), ...newAssignments],
      }));
      // Update model account count
      setModels(prev => prev.map(m =>
        m.id === assigningModel.id
          ? { ...m, accountCount: m.accountCount + selectedPortfolioIds.length }
          : m
      ));
      setSelectedPortfolioIds([]);
      window.alert(`Successfully assigned ${selectedPortfolioIds.length} portfolio(s) to ${assigningModel.name}`);
    } finally {
      setAssigningInProgress(false);
    }
  };

  // Unassign a portfolio from a model
  const handleUnassignPortfolio = async (modelId: number, assignmentId: number, portfolioName: string) => {
    if (!window.confirm(`Remove "${portfolioName}" from this model?`)) return;

    try {
      const assignment = assignedPortfolios[modelId]?.find(a => a.assignmentId === assignmentId);
      if (assignment) {
        await axios.post(`/api/advisor/models/${modelId}/unassign/${assignment.portfolioId}`);
      }
      setAssignedPortfolios(prev => ({
        ...prev,
        [modelId]: prev[modelId]?.filter(a => a.assignmentId !== assignmentId) || [],
      }));
      setModels(prev => prev.map(m =>
        m.id === modelId ? { ...m, accountCount: Math.max(0, m.accountCount - 1) } : m
      ));
    } catch (error) {
      // Mock success for demo
      setAssignedPortfolios(prev => ({
        ...prev,
        [modelId]: prev[modelId]?.filter(a => a.assignmentId !== assignmentId) || [],
      }));
      setModels(prev => prev.map(m =>
        m.id === modelId ? { ...m, accountCount: Math.max(0, m.accountCount - 1) } : m
      ));
    }
  };

  // Filter portfolios based on search and already assigned
  const getFilteredPortfolios = () => {
    // Safety check - ensure availablePortfolios is an array
    if (!Array.isArray(availablePortfolios)) {
      return [];
    }

    const assignedIds = assigningModel
      ? (assignedPortfolios[assigningModel.id] || []).map(a => a.portfolioId)
      : [];

    return availablePortfolios.filter(p => {
      // Exclude already assigned portfolios
      if (assignedIds.includes(p.id)) return false;
      // Filter by search
      if (portfolioSearch) {
        const search = portfolioSearch.toLowerCase();
        return (
          (p.portfolioName && p.portfolioName.toLowerCase().includes(search)) ||
          (p.portfolioCode && p.portfolioCode.toLowerCase().includes(search)) ||
          (p.householdName && p.householdName.toLowerCase().includes(search))
        );
      }
      return true;
    });
  };

  // Load assigned portfolios for all models on initial load
  useEffect(() => {
    models.forEach(model => {
      if (!assignedPortfolios[model.id]) {
        fetchAssignedPortfolios(model.id);
      }
    });
  }, [models]);

  return (
    <Container embedded={embedded}>
      {!embedded && (
        <Header>
          <Title>Model Portfolios</Title>
          <Button variant="primary" onClick={openNewModelModal}>
            <Plus className="w-4 h-4" /> Create Model
          </Button>
        </Header>
      )}

      <StatsGrid>
        <StatCard>
          <StatValue>{models.length}</StatValue>
          <StatLabel>Active Models</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>{formatCurrency(totalAum)}</StatValue>
          <StatLabel>Total AUM</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>{totalAccounts}</StatValue>
          <StatLabel>Accounts Assigned</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue style={{ color: modelsWithDrift > 0 ? '#eab308' : '#22c55e' }}>
            {modelsWithDrift}
          </StatValue>
          <StatLabel>Models Need Rebalancing</StatLabel>
        </StatCard>
      </StatsGrid>

      {models.length === 0 && !loading && (
        <EmptyState>
          <div style={{ marginBottom: '1rem' }}>
            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="3" width="18" height="18" rx="2" />
              <path d="M3 9h18" />
              <path d="M9 21V9" />
            </svg>
          </div>
          <h3 style={{ color: '#e2e8f0', marginBottom: '0.5rem', fontSize: '1.25rem' }}>No Model Portfolios</h3>
          <p style={{ color: '#64748b', marginBottom: '1.5rem', maxWidth: '400px' }}>
            Create your first model portfolio to define target allocations and start managing client accounts.
          </p>
          <Button variant="primary" onClick={openNewModelModal}>
            <Plus className="w-4 h-4" /> Create Your First Model
          </Button>
        </EmptyState>
      )}

      <ModelsGrid>
        {models.map((model) => (
          <ModelCard key={model.id} hasDrift={model.currentDrift > model.driftThreshold}>
            <CardActions>
              <CardActionButton variant="edit" onClick={() => handleEdit(model)}>
                <Edit2 className="w-4 h-4" />
              </CardActionButton>
              <CardActionButton variant="delete" onClick={() => handleDelete(model)}>
                <Trash2 className="w-4 h-4" />
              </CardActionButton>
            </CardActions>

            <ModelHeader>
              <ModelInfo>
                <ModelName>{model.name}</ModelName>
                <ModelCode>{model.code}</ModelCode>
              </ModelInfo>
              <StrategyBadge strategy={model.strategy}>{model.strategy}</StrategyBadge>
            </ModelHeader>

            <ModelStats>
              <ModelStat>
                <ModelStatValue>{formatCurrency(model.aum)}</ModelStatValue>
                <ModelStatLabel>AUM</ModelStatLabel>
              </ModelStat>
              <ModelStat>
                <ModelStatValue>{model.accountCount}</ModelStatValue>
                <ModelStatLabel>Accounts</ModelStatLabel>
              </ModelStat>
              <ModelStat>
                <ModelStatValue>{model.targetReturn}%</ModelStatValue>
                <ModelStatLabel>Target Return</ModelStatLabel>
              </ModelStat>
            </ModelStats>

            <AllocationBar>
              {model.allocations.map((alloc) => (
                <AllocationSegment
                  key={alloc.assetClass}
                  color={ALLOCATION_COLORS[alloc.assetClass] || '#64748b'}
                  width={alloc.currentPercent}
                />
              ))}
            </AllocationBar>

            <AllocationLegend>
              {model.allocations.map((alloc) => (
                <LegendItem key={alloc.assetClass}>
                  <LegendDot color={ALLOCATION_COLORS[alloc.assetClass] || '#64748b'} />
                  {alloc.assetClass} ({alloc.currentPercent.toFixed(1)}%)
                </LegendItem>
              ))}
            </AllocationLegend>

            {model.currentDrift > model.driftThreshold && (
              <DriftWarning>
                Drift: {model.currentDrift.toFixed(1)}% (exceeds {model.driftThreshold}% threshold)
                <Button variant="primary" style={{ marginLeft: 'auto', padding: '0.25rem 0.75rem', fontSize: '0.7rem' }}>
                  Rebalance
                </Button>
              </DriftWarning>
            )}

            {/* Assigned Portfolios Section */}
            <AssignedPortfoliosList>
              <AssignedCount>
                <Users className="w-3 h-3" style={{ display: 'inline', marginRight: '0.25rem' }} />
                {(assignedPortfolios[model.id] || []).length} portfolios assigned
              </AssignedCount>
              {(assignedPortfolios[model.id] || []).slice(0, 3).map((assigned) => (
                <AssignedPortfolioItem key={assigned.assignmentId}>
                  <PortfolioInfo>
                    <PortfolioName>{assigned.portfolioName}</PortfolioName>
                    <PortfolioMeta>
                      {assigned.portfolioCode} • {formatCurrency(assigned.aum)}
                      {assigned.assignmentType === 'SLEEVE' && ` • ${assigned.sleevePercentage}% sleeve`}
                    </PortfolioMeta>
                  </PortfolioInfo>
                  <UnassignButton onClick={() => handleUnassignPortfolio(model.id, assigned.assignmentId, assigned.portfolioName)}>
                    <Unlink className="w-3 h-3" />
                  </UnassignButton>
                </AssignedPortfolioItem>
              ))}
              {(assignedPortfolios[model.id] || []).length > 3 && (
                <div style={{ fontSize: '0.7rem', color: '#64748b', textAlign: 'center', marginTop: '0.5rem' }}>
                  +{(assignedPortfolios[model.id] || []).length - 3} more portfolios
                </div>
              )}
              <AssignButton onClick={() => openAssignModal(model)}>
                <Link2 className="w-4 h-4" />
                Assign Portfolios
              </AssignButton>
            </AssignedPortfoliosList>
          </ModelCard>
        ))}
      </ModelsGrid>

      {/* Create/Edit Model Modal */}
      <ModalBackdrop show={isModalOpen}>
        <ModalCard>
          <ModalHeader>
            <ModalTitle>{selectedModel ? 'Edit Model Portfolio' : 'Create Model Portfolio'}</ModalTitle>
            <ModalClose onClick={() => setIsModalOpen(false)}>
              <X className="w-5 h-5" />
            </ModalClose>
          </ModalHeader>
          <ModalBody>
            <FormRow>
              <FormGroup>
                <Label>Model Name</Label>
                <Input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Conservative Income"
                />
              </FormGroup>
              <FormGroup>
                <Label>Model Code</Label>
                <Input
                  type="text"
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                  placeholder="e.g., CONS-INC-01"
                />
              </FormGroup>
            </FormRow>

            <FormGroup>
              <Label>Description</Label>
              <TextArea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Describe the model portfolio strategy..."
              />
            </FormGroup>

            <FormRow>
              <FormGroup>
                <Label>Strategy</Label>
                <Select
                  value={formData.strategy}
                  onChange={(e) => setFormData({ ...formData, strategy: e.target.value as ModelPortfolio['strategy'] })}
                >
                  <option value="CONSERVATIVE">Conservative</option>
                  <option value="INCOME">Income</option>
                  <option value="BALANCED">Balanced</option>
                  <option value="GROWTH">Growth</option>
                  <option value="AGGRESSIVE">Aggressive</option>
                </Select>
              </FormGroup>
              <FormGroup>
                <Label>Status</Label>
                <Select
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value as ModelPortfolio['status'] })}
                >
                  <option value="ACTIVE">Active</option>
                  <option value="INACTIVE">Inactive</option>
                  <option value="DRAFT">Draft</option>
                </Select>
              </FormGroup>
            </FormRow>

            <FormRow>
              <FormGroup>
                <Label>Risk Level (1-10)</Label>
                <Input
                  type="number"
                  min="1"
                  max="10"
                  value={formData.riskLevel}
                  onChange={(e) => setFormData({ ...formData, riskLevel: parseInt(e.target.value) || 5 })}
                />
              </FormGroup>
              <FormGroup>
                <Label>Target Return (%)</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={formData.targetReturn}
                  onChange={(e) => setFormData({ ...formData, targetReturn: parseFloat(e.target.value) || 0 })}
                />
              </FormGroup>
            </FormRow>

            <FormRow>
              <FormGroup>
                <Label>Benchmark Index</Label>
                <Input
                  type="text"
                  value={formData.benchmarkIndex}
                  onChange={(e) => setFormData({ ...formData, benchmarkIndex: e.target.value })}
                  placeholder="e.g., S&P 500"
                />
              </FormGroup>
              <FormGroup>
                <Label>Drift Threshold (%)</Label>
                <Input
                  type="number"
                  step="0.5"
                  value={formData.driftThreshold}
                  onChange={(e) => setFormData({ ...formData, driftThreshold: parseFloat(e.target.value) || 5 })}
                />
              </FormGroup>
            </FormRow>
          </ModalBody>
          <ModalFooter>
            <Button variant="secondary" onClick={() => setIsModalOpen(false)}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleSave}>
              {selectedModel ? 'Save Changes' : 'Create Model'}
            </Button>
          </ModalFooter>
        </ModalCard>
      </ModalBackdrop>

      {/* Portfolio Assignment Modal */}
      <ModalBackdrop show={isAssignModalOpen}>
        <ModalCard>
          <ModalHeader>
            <ModalTitle>
              <Link2 className="w-5 h-5" style={{ marginRight: '0.5rem' }} />
              Assign Portfolios to {assigningModel?.name}
            </ModalTitle>
            <ModalClose onClick={() => setIsAssignModalOpen(false)}>
              <X className="w-5 h-5" />
            </ModalClose>
          </ModalHeader>
          <ModalBody>
            {/* Currently Assigned */}
            {assigningModel && (assignedPortfolios[assigningModel.id] || []).length > 0 && (
              <FormGroup>
                <Label>Currently Assigned ({(assignedPortfolios[assigningModel.id] || []).length})</Label>
                <PortfolioListContainer style={{ maxHeight: '150px' }}>
                  {(assignedPortfolios[assigningModel.id] || []).map((assigned) => (
                    <PortfolioSelectItem key={assigned.assignmentId}>
                      <PortfolioDetails>
                        <PortfolioName>{assigned.portfolioName}</PortfolioName>
                        <PortfolioMeta>
                          {assigned.portfolioCode}
                          {assigned.assignmentType === 'SLEEVE' && ` • ${assigned.sleevePercentage}% sleeve`}
                        </PortfolioMeta>
                      </PortfolioDetails>
                      <PortfolioAum>{formatCurrency(assigned.aum)}</PortfolioAum>
                      <UnassignButton
                        onClick={() => handleUnassignPortfolio(assigningModel.id, assigned.assignmentId, assigned.portfolioName)}
                      >
                        <Unlink className="w-3 h-3" /> Remove
                      </UnassignButton>
                    </PortfolioSelectItem>
                  ))}
                </PortfolioListContainer>
              </FormGroup>
            )}

            {/* Assignment Type Selection */}
            <FormGroup>
              <Label>Assignment Type</Label>
              <AssignmentTypeSelect>
                <AssignmentTypeOption
                  selected={assignmentType === 'FULL'}
                  onClick={() => setAssignmentType('FULL')}
                >
                  <AssignmentTypeTitle>Full Assignment</AssignmentTypeTitle>
                  <AssignmentTypeDesc>100% of portfolio follows this model</AssignmentTypeDesc>
                </AssignmentTypeOption>
                <AssignmentTypeOption
                  selected={assignmentType === 'SLEEVE'}
                  onClick={() => setAssignmentType('SLEEVE')}
                >
                  <AssignmentTypeTitle>Sleeve Assignment</AssignmentTypeTitle>
                  <AssignmentTypeDesc>Partial allocation to this model</AssignmentTypeDesc>
                </AssignmentTypeOption>
              </AssignmentTypeSelect>
            </FormGroup>

            {assignmentType === 'SLEEVE' && (
              <FormGroup>
                <Label>Sleeve Percentage (%)</Label>
                <Input
                  type="number"
                  min="1"
                  max="100"
                  value={sleevePercentage}
                  onChange={(e) => setSleevePercentage(parseInt(e.target.value) || 0)}
                />
              </FormGroup>
            )}

            {/* Search and Select Portfolios */}
            <FormGroup>
              <Label>Select Portfolios to Assign</Label>
              <SearchInput
                type="text"
                placeholder="Search by portfolio name, code, or household..."
                value={portfolioSearch}
                onChange={(e) => setPortfolioSearch(e.target.value)}
              />
              <PortfolioListContainer>
                {getFilteredPortfolios().length === 0 ? (
                  <EmptyState>
                    {portfolioSearch ? 'No portfolios match your search' : 'All portfolios are already assigned'}
                  </EmptyState>
                ) : (
                  getFilteredPortfolios().map((portfolio) => (
                    <PortfolioSelectItem
                      key={portfolio.id}
                      selected={selectedPortfolioIds.includes(portfolio.id)}
                      onClick={() => togglePortfolioSelection(portfolio.id)}
                    >
                      <PortfolioCheckbox checked={selectedPortfolioIds.includes(portfolio.id)}>
                        {selectedPortfolioIds.includes(portfolio.id) && '✓'}
                      </PortfolioCheckbox>
                      <PortfolioDetails>
                        <PortfolioName>{portfolio.portfolioName}</PortfolioName>
                        <PortfolioMeta>
                          {portfolio.portfolioCode}
                          {portfolio.householdName && ` • ${portfolio.householdName}`}
                          {portfolio.riskTolerance && ` • ${portfolio.riskTolerance}`}
                        </PortfolioMeta>
                      </PortfolioDetails>
                      <PortfolioAum>{formatCurrency(portfolio.aum)}</PortfolioAum>
                    </PortfolioSelectItem>
                  ))
                )}
              </PortfolioListContainer>
            </FormGroup>

            {selectedPortfolioIds.length > 0 && (
              <div style={{ fontSize: '0.875rem', color: '#a78bfa', marginTop: '0.5rem' }}>
                {selectedPortfolioIds.length} portfolio(s) selected for {assignmentType.toLowerCase()} assignment
                {assignmentType === 'SLEEVE' && ` at ${sleevePercentage}%`}
              </div>
            )}
          </ModalBody>
          <ModalFooter>
            <Button variant="secondary" onClick={() => setIsAssignModalOpen(false)}>
              Close
            </Button>
            <Button
              variant="primary"
              onClick={handleAssignPortfolios}
              disabled={selectedPortfolioIds.length === 0 || assigningInProgress}
            >
              {assigningInProgress ? 'Assigning...' : `Assign ${selectedPortfolioIds.length} Portfolio(s)`}
            </Button>
          </ModalFooter>
        </ModalCard>
      </ModalBackdrop>
    </Container>
  );
};

export default ModelPortfoliosScreen;
